import React, { useEffect, useState, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, BarChart3Icon, ArrowRightIcon, ShieldIcon, FlameIcon } from 'lucide-react';
function PriceBlock() {
  const [spots, setSpots] = useState(15);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
      }
    }, {
      threshold: 0.5
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const interval = setInterval(() => {
      setSpots(prev => prev > 3 ? prev - 1 : 3);
    }, 8000);
    return () => clearInterval(interval);
  }, [started]);
  return <div ref={ref} className="w-full grid grid-cols-2 gap-3 mb-6 rounded-2xl overflow-hidden border border-gray-200" data-id="element-355">
      {/* Left: Spots */}
      <div className="bg-white p-4 md:p-6 text-center" data-id="element-356">
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase mb-2 flex items-center justify-center gap-1.5" style={{
        color: '#e76f51'
      }} data-id="element-357">
          <FlameIcon className="w-4 h-4" data-id="element-358" /> CUPOS HOY
        </p>
        <p className="text-4xl md:text-5xl font-bold tabular-nums" style={{
        color: '#1A1614'
      }} data-id="element-359">
          {spots}
        </p>
        <p className="text-xs mt-1" style={{
        color: '#5a5248'
      }} data-id="element-360">
          Al llegar a 0 sube el precio
        </p>
      </div>
      {/* Right: Price */}
      <div className="bg-white p-4 md:p-6 text-center" data-id="element-361">
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase mb-2 flex items-center justify-center gap-1.5" style={{
        color: '#07c86e'
      }} data-id="element-362">
          💰 PRECIO ACTUAL
        </p>
        <div className="flex items-center justify-center gap-2 mb-1" data-id="element-363">
          <span className="md:text-lg line-through text-[18px]" style={{
          color: '#a8a29e'
        }} data-id="element-364">
            $19
          </span>
          <span className="md:text-4xl font-bold text-[32px]" style={{
          color: '#e76f51'
        }} data-id="element-365">
            $6.39
          </span>
        </div>
        <div className="flex items-center justify-center gap-2" data-id="element-366">
          <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{
          backgroundColor: '#e76f51'
        }} data-id="element-367">
            -66%
          </span>
          <span className="text-xs" style={{
          color: '#5a5248'
        }} data-id="element-368">
            Ahorras $12.61 USD
          </span>
        </div>
      </div>
    </div>;
}
export function WhatsIncluded() {
  return <>
      {/* PRUEBA DE MERCADO */}
      <section className="py-24 md:py-32 px-4 pt-[70px] pb-[70px]" style={{
      backgroundColor: '#ffffff'
    }} data-id="element-369">
        <div className="max-w-4xl mx-auto" data-id="element-370">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} data-id="element-371">
            <span className="text-sm font-bold tracking-[0.2em] uppercase mb-4 inline-block" style={{
            color: '#e76f51'
          }} data-id="element-372">
              📊 LA TENDENCIA ES REAL
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-10" style={{
            color: '#1A1614'
          }} data-id="element-373">
              El mercado ya lo confirma
            </h2>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5
          }} className="rounded-3xl overflow-hidden shadow-xl mb-12" data-id="element-374">
              <img src="https://cdn.magicpatterns.com/uploads/9RPfNAeTWom1yKu3Ydhsqi/Diseno_sin_titulo_(4).png" alt="Personas disfrutando cinnamon rolls XL — tendencia viral" className="w-full object-cover" style={{
              maxHeight: '420px'
            }} data-id="element-375" />
            </motion.div>

            <p className="text-xl mb-8" style={{
            color: '#5a5248'
          }} data-id="element-376">
              La repostería visual y los productos XL se han convertido en
              tendencia dominante en redes sociales.
            </p>

            <p className="text-xl font-bold italic mb-6" style={{
            color: '#e76f51',
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-377">
              Las marcas que trabajan con:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-id="element-378">
              {[{
              text: 'Menús rotativos.',
              img: 'https://cdn.magicpatterns.com/uploads/vxCFFECdRWvwNjK2CCk8QD/MKI--VAc29kF8UkcNwR3z_(1).png'
            }, {
              text: 'Ediciones coleccionables.',
              img: 'https://cdn.magicpatterns.com/uploads/5cdjPqY7xq7yHVc8yTVUJi/cP9Zrsh60ldu0iro7r---.png'
            }, {
              text: 'Formatos XL memorables.',
              img: 'https://cdn.magicpatterns.com/uploads/5g2FbSE6GAin3WrSvPjUTH/oSDkGh-kP619f470j3k1O.png'
            }].map((item, i) => <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100 text-center" style={{
              minHeight: '180px'
            }} data-id="element-379">
                  <img src={item.img} alt={item.text} className="absolute inset-0 w-full h-full object-cover" data-id="element-380" />
                  <div className="absolute inset-0" style={{
                background: 'linear-gradient(to top, rgba(26,22,20,0.75) 0%, rgba(26,22,20,0.25) 100%)'
              }} data-id="element-381" />
                  <div className="relative z-10 flex items-end justify-center h-full p-6" data-id="element-382">
                    <p className="text-lg font-bold text-white drop-shadow-lg" data-id="element-383">
                      {item.text}
                    </p>
                  </div>
                </div>)}
            </div>

            <p className="text-xl font-bold mb-4" style={{
            color: '#1A1614'
          }} data-id="element-384">
              Aumentan percepción de valor y frecuencia de compra.
            </p>
            <p className="text-lg mb-2" style={{
            color: '#5a5248'
          }} data-id="element-385">
              No es casualidad.
            </p>
            <p className="text-2xl font-bold" style={{
            color: '#e76f51'
          }} data-id="element-386">
              Es psicología aplicada al producto.
            </p>
          </motion.div>
        </div>
      </section>

      {/* EL PRODUCTO - E-COMMERCE STYLE */}
      <section id="producto" className="py-16 md:py-32 px-4" style={{
      backgroundColor: '#F5EDE4'
    }} data-id="element-387">
        <div className="max-w-6xl mx-auto" data-id="element-388">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center" data-id="element-389">
            {/* LEFT COLUMN: PRODUCT PHOTO (Hidden on mobile to control exact placement in right column) */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="hidden lg:block lg:w-1/2" data-id="element-390">
              <img src="https://cdn.magicpatterns.com/uploads/m4LWU9UjfJ287yR4wVx6ii/Diseno_sin_titulo_(2).png" alt="Cinnamon Rolls XL en dispositivos" className="w-full rounded-3xl shadow-2xl object-cover max-h-[280px] lg:max-h-[600px]" data-id="element-391" />
            </motion.div>

            {/* RIGHT COLUMN: PRODUCT INFO */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-392">
              {/* 1. Subtitle */}
              <span className="md:text-sm font-bold tracking-[0.2em] uppercase mb-3 text-[14px]" style={{
              color: '#e76f51'
            }} data-id="element-393">
                DOMINA EL MÉTODO
              </span>

              {/* 2. Title */}
              <h2 className="text-3xl md:text-5xl font-bold mb-2 leading-tight" style={{
              color: '#1A1614'
            }} data-id="element-394">
                Cinnamon Rolls XL
              </h2>

              {/* 3. Subtitle Italic */}
              <p className="text-xl md:text-2xl italic mb-4" style={{
              color: '#e76f51',
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-395">
                Estilo New York — Edición Coleccionable
              </p>

              {/* 4. Description */}
              <p className="text-base md:text-lg mb-6 max-w-md" style={{
              color: '#5a5248'
            }} data-id="element-396">
                Un sistema creativo diseñado para que tus cinnamon rolls tengan
                presencia de bakery profesional moderna.
              </p>

              {/* 5. Photo on Mobile (Hidden on Desktop, shown here to be between desc and includes) */}
              <div className="w-full block lg:hidden mb-6" data-id="element-397">
                <img src="https://cdn.magicpatterns.com/uploads/m4LWU9UjfJ287yR4wVx6ii/Diseno_sin_titulo_(2).png" alt="Cinnamon Rolls XL en dispositivos" className="w-full rounded-2xl shadow-xl object-cover max-h-[280px]" data-id="element-398" />
              </div>

              {/* 6. Includes */}
              <div className="w-full mb-6" data-id="element-399">
                <p className="text-base md:text-lg font-bold mb-3" style={{
                color: '#1A1614'
              }} data-id="element-400">
                  Incluye:
                </p>
                <ul className="space-y-2 text-left inline-block lg:block" data-id="element-401">
                  {['50 recetas adaptadas a formato XL real', 'Método paso a paso para principiantes', 'Técnica avanzada para lograr altura estable', 'Sistema de menú rotativo y edición coleccionable', 'Arquitectura de masa y relleno profesional'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-402">
                      <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#07c86e'
                  }} data-id="element-403" />
                      <span className="text-sm md:text-base font-medium" style={{
                    color: '#1A1614'
                  }} data-id="element-404">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* 7. Bonuses */}
              <div className="w-full rounded-xl p-4 mb-8 text-left inline-block lg:block" style={{
              backgroundColor: 'rgba(231,111,81,0.1)'
            }} data-id="element-405">
                <p className="text-lg md:text-xl font-bold flex gap-2 items-center justify-center" style={{
                color: '#e76f51'
              }} data-id="element-406">
                  🎁 + 3 Bonos Gratis incluidos
                </p>
              </div>

              {/* 8. Price Block — Two columns */}
              <PriceBlock data-id="element-407" />

              {/* 9. CTA Button */}
              <a href="https://homemasterglobal.com/cart/43531718492238:1" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-center gap-3 font-bold text-lg md:text-xl py-4 md:py-5 px-8 rounded-full transition-all duration-300 mb-3 no-underline" style={{
              backgroundColor: '#e76f51',
              color: '#ffffff',
              boxShadow: '0 8px 24px rgba(231,111,81,0.3)'
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f08565';
              e.currentTarget.style.transform = 'scale(1.02)';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#e76f51';
              e.currentTarget.style.transform = 'scale(1)';
            }} data-id="element-408">
                Sí, quiero dominar los Cinnamon Rolls XL
                <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-409" />
              </a>

              {/* 10. Guarantee */}
              <p className="text-xs md:text-sm flex lg:justify-start gap-1.5 w-full items-center justify-center" style={{
              color: '#5a5248'
            }} data-id="element-410">
                Garantía 7 días · Descarga inmediata · Pago Seguro
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BONOS GRATIS */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: '#ffffff'
    }} data-id="element-411">
        <div className="max-w-4xl mx-auto" data-id="element-412">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="text-center mb-10" data-id="element-413">
            <span className="inline-block md:text-sm font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full mb-4 text-[14px]" style={{
            backgroundColor: 'rgba(231,111,81,0.1)',
            color: '#e76f51'
          }} data-id="element-414">
              BONOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{
            color: '#1A1614'
          }} data-id="element-415">
              Lo que recibes hoy — todo incluido
            </h2>
            <p className="text-base md:text-lg max-w-2xl mx-auto" style={{
            color: '#5a5248'
          }} data-id="element-416">
              Al adquirir tu Edición Coleccionable no solo recibes el método
              completo. También incluimos 3 bonos que multiplican tus
              resultados.
            </p>
          </motion.div>

          <div className="space-y-6" data-id="element-417">
            {[{
            num: 1,
            title: 'Master en Rellenos de Cinnamon Rolls XL',
            desc: 'Aprende a crear rellenos cremosos, abundantes y con textura perfecta que no dañen la estructura de tu roll. Desde clásicos hasta sabores en tendencia.',
            img: 'https://cdn.magicpatterns.com/uploads/bk3kLH5w61yQsH4rFdHWLL/ChatGPT_Image_17_mar_2026,_05_53_55_p.m..png',
            price: '$9 USD'
          }, {
            num: 2,
            title: 'Master en Decoración de Cinnamon Rolls XL',
            desc: 'Técnicas de decoración profesional para que cada roll parezca salido de una vitrina de bakery moderna. Toppings, glaseados y presentación estratégica.',
            img: 'https://cdn.magicpatterns.com/uploads/9D26BQk14aCKVxdfE1huGa/ChatGPT_Image_17_mar_2026,_06_04_35_p.m..png',
            price: '$9 USD'
          }, {
            num: 3,
            title: 'Rastreador de Tendencias de Cinnamon Rolls XL',
            desc: 'Identifica qué sabores y estilos están en tendencia real para crear ediciones limitadas que se vendan rápido y generen expectativa.',
            img: 'https://cdn.magicpatterns.com/uploads/rBtNgU6w3BCWgDMuC6fySm/ChatGPT_Image_17_mar_2026,_06_17_01_p.m..png',
            price: '$7 USD'
          }].map((bonus, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex flex-col sm:flex-row items-center gap-5 bg-white rounded-2xl p-5 md:p-6 shadow-md border border-gray-100" data-id="element-418">
                {/* Bonus Image */}
                <img src={bonus.img} alt={bonus.title} className="w-28 h-36 md:w-32 md:h-40 object-contain flex-shrink-0 rounded-xl" data-id="element-419" />

                {/* Bonus Info */}
                <div className="flex-1 text-center sm:text-left" data-id="element-420">
                  <p className="md:text-base font-bold tracking-wider uppercase mb-1 text-[16px]" style={{
                color: '#e76f51'
              }} data-id="element-421">
                    BONO {bonus.num}
                  </p>
                  <h3 className="text-lg md:text-xl font-bold mb-2" style={{
                color: '#1A1614'
              }} data-id="element-422">
                    {bonus.title}
                  </h3>
                  <p className="text-sm md:text-base mb-3" style={{
                color: '#5a5248'
              }} data-id="element-423">
                    {bonus.desc}
                  </p>
                </div>

                {/* Price Badge */}
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-424">
                  <span className="text-sm line-through mb-1" style={{
                color: '#a8a29e'
              }} data-id="element-425">
                    {bonus.price}
                  </span>
                  <span className="text-lg font-bold px-4 py-1 rounded-full" style={{
                color: '#07c86e',
                backgroundColor: 'rgba(7,200,110,0.1)'
              }} data-id="element-426">
                    GRATIS
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
    </>;
}